"use client";

import type { LinkProps as NextLinkProps } from "@/i18n/navigation";
import type { IImage } from "@components/types/media";
import type { Media } from "@mw/shared/payload-types";
import type { VariantProps } from "class-variance-authority";
import type { HTMLAttributeAnchorTarget } from "react";
import type React from "react";

import { getImageProps } from "@utilities/payload";
import { getUrl } from "@utilities/links";
import { logger } from "@utilities/logger";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { cva } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { useState, useEffect } from "react";
import NextImage from "next/image";
import { getUTMs } from "@/app/_analytics/utmHandler";

import { Link as LocalizedLink } from "@/i18n/navigation";
import NextLink from "next/link";

import type { buttonVariants } from "./Button";

import { Button } from "./Button";

export interface ILinkIconSource {
  media?: Media | string | null;
  // centralIcon?: ... (future extension)
}

export interface ILink {
  appearance?: "ghost" | "primary" | "secondary" | "elevated" | null;
  ariaLabel?: string;
  description?: null | string;
  href: NextLinkProps["href"];
  iconSource?: ILinkIconSource | null;
  img?: IImage | null;
  label?: string;
  rel?: string;
  target?: HTMLAttributeAnchorTarget;
  copyUTMTags?: boolean;
}

const linkVariants = cva("inline-flex justify-center items-center rounded-sm", {
  variants: {
    variant: {
      expressive: "",
      functional: "underline underline-offset-2",
      clean: "no-underline",
    },
    inverse: {
      true: "text-inverse hover:text-lightest effect-focus-inverse group-hover:text-lightest",
      false: "hover:text-light group-hover:text-light effect-focus",
    },
    size: {
      xs: "",
      sm: "",
      md: "",
      lg: "",
    },
    disabled: {
      true: "cursor-not-allowed pointer-events-none",
      false: "",
    },
  },
  compoundVariants: [
    // Functional variant
    {
      variant: "functional",
      size: "sm",
      className: "f-ui-05",
    },
    {
      variant: "functional",
      size: "lg",
      className: "f-ui-02",
    },
    {
      variant: "functional",
      inverse: false,
      className: "decoration-darker",
    },
    {
      variant: "functional",
      inverse: true,
      className: "decoration-lighter",
    },
    // Expressive variant
    {
      variant: "expressive",
      size: "xs",
      className: "f-ui-05",
    },
    {
      variant: "expressive",
      size: "sm",
      className: "f-ui-04",
    },
    {
      variant: "expressive",
      size: "md",
      className: "f-ui-02",
    },
    {
      variant: "expressive",
      size: "lg",
      className: "f-ui-03",
    },
  ],
  defaultVariants: {
    size: "sm",
    variant: "expressive",
    inverse: false,
    disabled: false,
  },
});

export interface ILinkProps extends ILink, NextLinkProps, VariantProps<typeof linkVariants> {
  asButton?: boolean;
  buttonVariants?: VariantProps<typeof buttonVariants>;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: ICON_ALIGN_OPTIONS;
  label?: string;
  onExternalLoading?: () => void;
  ref?: React.Ref<HTMLAnchorElement | null>;
  showExternalLoading?: boolean;
}

export function BaseLink(props: NextLinkProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { url, isExternal, cannotLocalize } = getUrl(props.href);

  if (isExternal) {
    const { popover: _, ...rest } = props;
    return <NextLink {...rest} href={url} prefetch={false} />;
  }

  // By default we prefetch internal links on hover, but respect explicit `true` or `false` values.
  const prefetch = props.prefetch || (props.prefetch === undefined && isHovered);

  const handleMouseEnter = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setIsHovered(true);
    props.onMouseEnter?.(event);
  };

  if (cannotLocalize) {
    const { popover: _, ...rest } = props;
    return <NextLink {...rest} href={url} onMouseEnter={handleMouseEnter} prefetch={prefetch} />;
  }

  return <LocalizedLink {...props} href={url} onMouseEnter={handleMouseEnter} prefetch={prefetch} />;
}

const Link = ({
  className,
  variant,
  size,
  inverse,
  disabled = false,
  icon,
  iconSource,
  asButton,
  buttonVariants,
  iconPosition = ICON_ALIGN_OPTIONS.BEFORE,
  label,
  children,
  img,
  showExternalLoading = false,
  copyUTMTags = false,
  ...props
}: ILinkProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [utmEnhancedHref, setUtmEnhancedHref] = useState<string | null>(null);

  // Append UTM params after mount if copyUTMTags is enabled (client-side only)
  useEffect(() => {
    if (!copyUTMTags || typeof props.href !== "string") {
      setUtmEnhancedHref(null);
      return;
    }

    try {
      const utms = getUTMs();
      const url = new URL(props.href, window.location.origin);

      // Only append if UTM values exist
      if (utms.utmSource) url.searchParams.set("utm_source", utms.utmSource);
      if (utms.utmMedium) url.searchParams.set("utm_medium", utms.utmMedium);
      if (utms.utmCampaign) url.searchParams.set("utm_campaign", utms.utmCampaign);
      if (utms.utmContent) url.searchParams.set("utm_content", utms.utmContent);
      if (utms.utmTerm) url.searchParams.set("utm_term", utms.utmTerm);

      setUtmEnhancedHref(url.href);
    } catch (e) {
      // If URL parsing fails, use original href
      logger.warn("[Link] Failed to append UTM params", e);
      setUtmEnhancedHref(null);
    }
  }, [props.href, copyUTMTags]);

  // Use UTM-enhanced href if available, otherwise use props.href
  const href = utmEnhancedHref ?? props.href;

  const { isExternal } = getUrl(href);
  const prefetch = disabled ? false : undefined;

  // Derive icon from iconSource if not explicitly provided
  const isSmallButton = asButton && buttonVariants?.size === "sm";
  const iconProps = iconSource?.media ? getImageProps(iconSource.media, { placeholder: true }) : null;
  const iconSizeClass = isSmallButton ? "tw-size-3.5 tw-shrink-0" : "tw-size-5 tw-shrink-0";
  const derivedIcon = icon ?? (iconProps ? <NextImage {...iconProps} alt='' className={iconSizeClass} /> : undefined);

  function handleButtonClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (isExternal && showExternalLoading && props.target !== "_blank") {
      setIsLoading(true);
    }
    props.onClick?.(event);
  }

  return asButton ? (
    <Button
      asChild
      className={className}
      disabled={disabled || false}
      link
      {...buttonVariants}
      icon={derivedIcon}
      iconPosition={iconPosition}
    >
      <BaseLink aria-label={props.ariaLabel} prefetch={prefetch} {...props} href={href} onClick={handleButtonClick}>
        {isLoading ? (
          <LoaderCircle className='animate-spin' size={14} />
        ) : (
          <>
            {derivedIcon && iconPosition === ICON_ALIGN_OPTIONS.BEFORE && derivedIcon}
            {label || children}
            {derivedIcon && iconPosition === ICON_ALIGN_OPTIONS.AFTER && derivedIcon}
          </>
        )}
      </BaseLink>
    </Button>
  ) : (
    <BaseLink
      aria-label={props.ariaLabel}
      className={cn(linkVariants({ size, variant, inverse, className, disabled }))}
      prefetch={prefetch}
      {...props}
      href={href}
    >
      <>
        {derivedIcon && iconPosition === ICON_ALIGN_OPTIONS.BEFORE && <span className='pr-4'>{derivedIcon}</span>}
        {label || children}
        {derivedIcon && iconPosition === ICON_ALIGN_OPTIONS.AFTER && <span className='pl-4'>{derivedIcon}</span>}
      </>
    </BaseLink>
  );
};

Link.displayName = "Link";

export { getUrl, sanitizeUrl } from "@utilities/links";
export { Link, linkVariants };
